import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { MeshDistortMaterial, OrbitControls } from '@react-three/drei';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
`;

const CubeContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  pointer-events: none;
  opacity: 0.8;
  z-index: 0;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 4rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  background: var(--gradient-ocean);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  color: var(--light-gray);
  font-size: 1.25rem;
  margin-bottom: 60px;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1200px;
`;

const ScreenCard = styled(Link)`
  background: rgba(26, 26, 26, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.color || 'var(--kuutio-blue)'};
    box-shadow: 0 8px 32px ${props => props.color ? props.color + '40' : 'rgba(0, 153, 255, 0.25)'};
    
    &::before {
      opacity: 0.1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.color || 'var(--kuutio-blue)'};
    opacity: 0.05;
    transition: opacity 0.3s ease;
  }
`;

const ScreenTitle = styled.h3`
  color: var(--white);
  font-size: 1.5rem;
  margin-bottom: 8px;
  position: relative;
`;

const ScreenDescription = styled.p`
  color: var(--gray);
  font-size: 0.875rem;
  position: relative;
`;

const ScreenBadge = styled.div`
  display: inline-block;
  background: ${props => props.color || 'var(--kuutio-blue)'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  margin-top: 12px;
  position: relative;
`;

const InfoPanel = styled.div`
  background: rgba(26, 26, 26, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  margin-top: 60px;
  max-width: 600px;
  text-align: center;
`;

const InfoText = styled.p`
  color: var(--light-gray);
  font-size: 0.875rem;
  line-height: 1.6;
`;

// 3D Cube Component for background
function BackgroundCube() {
  const meshRef = useRef();
  const particleRefs = useRef([]);
  
  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    
    if (meshRef.current) {
      // Smooth, slow rotation
      meshRef.current.rotation.x = Math.sin(time * 0.2) * 0.3;
      meshRef.current.rotation.y = time * 0.1;
      meshRef.current.rotation.z = Math.cos(time * 0.15) * 0.2;
    }
    
    // Animate particles in orbital motion
    particleRefs.current.forEach((particle, i) => {
      if (particle) {
        const angle = time * 0.3 + i * (Math.PI * 2 / 8);
        const radius = 4 + Math.sin(time + i) * 0.5;
        particle.position.x = Math.cos(angle) * radius;
        particle.position.y = Math.sin(angle * 1.5) * radius * 0.7;
        particle.position.z = Math.sin(angle) * radius;
        
        // Pulsing scale
        const scale = 1 + Math.sin(time * 2 + i) * 0.3;
        particle.scale.setScalar(scale);
      }
    });
  });

  return (
    <group>
      {/* 3D cube with proper shading */}
      <mesh ref={meshRef} scale={4}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          color="#0066cc"
          metalness={0.7}
          roughness={0.3}
          emissive="#001122"
          emissiveIntensity={0.05}
        />
      </mesh>
    </group>
  );
}

function ControlPanel() {
  const screens = [
    {
      path: '/screen/data',
      title: 'DATA',
      description: 'Reaaliaikaiset datavirrat ja metriikat',
      color: 'var(--kuutio-blue)',
      badge: 'Näyttö 1'
    },
    {
      path: '/screen/futures',
      title: 'TULEVAISUUDET',
      description: 'Skenaariot ja tulevaisuuspolut',
      color: 'var(--kuutio-yellow)',
      badge: 'Näyttö 2'
    },
    {
      path: '/screen/solutions',
      title: 'RATKAISUT',
      description: 'Innovaatiot ja toimenpide-ehdotukset',
      color: 'var(--kuutio-green)',
      badge: 'Näyttö 4'
    },
    {
      path: '/table',
      title: 'KESKUSPÖYTÄ',
      description: '3D Kuutio-visualisointi',
      color: 'var(--kuutio-purple)',
      badge: 'Pöytänäyttö'
    },
    {
      path: '/tablet',
      title: 'TABLETTI',
      description: 'Osallistujan käyttöliittymä',
      color: 'var(--gradient-neon)',
      badge: 'Mobiili'
    },
    {
      path: '/comments',
      title: 'KOMMENTIT',
      description: 'Facilitaattorin näkymä - kaikki syötteet reaaliajassa',
      color: '#ff6b35',
      badge: 'Admin'
    },
    {
      path: '/statmap',
      title: 'TILASTOKARTTA',
      description: 'Tilastokeskus PxAPI — Suomi-choropleth POC',
      color: '#3d9ddd',
      badge: 'Labs'
    }
  ];

  return (
    <Container>
      {/* Background 3D Cube */}
      <CubeContainer>
        <Canvas
          camera={{ position: [0, 0, 8], fov: 45 }}
          gl={{ antialias: true, alpha: true }}
        >
          {/* Minimal lighting for ominous effect */}
          <ambientLight intensity={0.1} />
          <directionalLight 
            position={[5, 5, 5]} 
            intensity={0.8}
            castShadow={false}
          />
          <pointLight position={[-3, 3, 4]} intensity={0.4} color="#0088ff" />
          
          <BackgroundCube />
        </Canvas>
      </CubeContainer>

      {/* Content */}
      <ContentWrapper>
        <Title>KUUTIO</Title>
        <Subtitle>Valitse näyttö avataksesi sen uuteen ikkunaan</Subtitle>
        
        <Grid>
          {screens.map(screen => (
            <ScreenCard 
              key={screen.path}
              to={screen.path}
              color={screen.color}
              target="_blank"
            >
              <ScreenTitle>{screen.title}</ScreenTitle>
              <ScreenDescription>{screen.description}</ScreenDescription>
              <ScreenBadge color={screen.color}>{screen.badge}</ScreenBadge>
            </ScreenCard>
          ))}
        </Grid>

        <InfoPanel>
          <InfoText>
            💡 <strong>Vinkki:</strong> Avaa jokainen näyttö omaan ikkunaansa ja siirrä ne vastaaville näytöille. 
            Keskuspöytä toimii parhaiten 4K-resoluutiolla. Tablettinäkymä on optimoitu mobiililaitteille.
          </InfoText>
        </InfoPanel>
      </ContentWrapper>
    </Container>
  );
}

export default ControlPanel;